import { library } from '@fortawesome/fontawesome-svg-core';
import { far } from '@fortawesome/free-regular-svg-icons';
import { fas } from '@fortawesome/free-solid-svg-icons';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import React, { Suspense, useEffect } from 'react';
import ReactDOM from 'react-dom';
import ReactGA from 'react-ga4';

import { GOOGLE_ANALYTICS_CONFIG } from './constants';
import { PageId } from './enum/PageId';
import { useEnvironment } from './hooks/use-environment';
import { ForgotPasswordPage } from './pages/Authentication/ForgotPasswordPage';
import { ForgotPasswordSubmissionPage } from './pages/Authentication/ForgotPasswordSubmissionPage';
import { ForgotPasswordSubmissionSuccessPage } from './pages/Authentication/ForgotPasswordSubmissionSuccessPage';
import { ForgotPasswordSuccessPage } from './pages/Authentication/ForgotPasswordSuccessPage';
import { ForgotUsernamePage } from './pages/Authentication/ForgotUsernamePage';
import { ForgotUsernameSuccessPage } from './pages/Authentication/ForgotUsernameSuccessPage';
import { LoginPage } from './pages/Authentication/LoginPage';
import { EndOfLevelTestPage } from './pages/EndOfLevelTest/EndOfLevelTestPage';
import { ErrorPage } from './pages/Error/Error';
import { ForbiddenPage } from './pages/Forbidden/Forbidden';
import { HelpPage } from './pages/Help/Help';
import { HomePage } from './pages/HomePage';
import { LessonPage } from './pages/Lesson/LessonPage';
import { LessonIndexPage } from './pages/LessonIndex/LessonIndexPage';
import { LevelPage } from './pages/Level/LevelPage';
import { LevelsPage } from './pages/Levels/LevelsPage';
import { NotFoundPage } from './pages/NotFound/NotFound';
import { OnboardingEmergencyContactPage } from './pages/Onboarding/OnboardingEmergencyContactPage';
import { OnboardingRecommendationsPage } from './pages/Onboarding/OnboardingRecommendationsPage';
import { OnboardingVideoTutorialPage } from './pages/Onboarding/OnboardingVideoTutorialPage';
import { OnboardingWelcomePage } from './pages/Onboarding/OnboardingWelcomePage';
import { PlacementTestIntroductionPage } from './pages/PlacementTest/PlacementTestIntroductionPage';
import { PlacementTestPage } from './pages/PlacementTest/PlacementTestPage';
import { PlacementTestPlayerPage } from './pages/PlacementTest/PlacementTestPlayerPage';
import { PlacementTestResultPage } from './pages/PlacementTest/PlacementTestResultPage';
import { PlatformTourVideoPage } from './pages/PlatformTourVideo/PlatformTourVideoPage';
import { Play2LearnOnboardingPage } from './pages/Play2Learn/Play2LearnOnboardingPage';
import { Play2LearnZoomPage } from './pages/Play2LearnZoom/Play2LearnZoom';
import { PreClassActivityPage } from './pages/PreClassActivity/PreClassActivity';
import { ProfilePage } from './pages/Profile/ProfilePage';
import { LiveClassHistoryPage } from './pages/Progress/LiveClassHistoryPage';
import { SharePage } from './pages/Share/Share';
import { SuspendedPage } from './pages/Suspended/Suspended';
import { UnitPage } from './pages/Unit/UnitPage';
import { ZoomInstructionsPage } from './pages/ZoomInstructions/ZoomInstructions';
import { AppProviders } from './providers/AppProviders';
import reportWebVitals from './report-web-vitals';
import '@fontsource/inter';
import '@fontsource/baloo-2';
import '@fontsource/gochi-hand';
import '@fontsource/inter/700.css';
import '@fontsource/baloo-2/700.css';
import './styles/bootstrap/custom.scss';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import './styles/index.scss';
import { fixScreenHeight } from './utils';

library.add(fas, far);

fixScreenHeight();

const rawData = document.getElementById('root')?.getAttribute('data') || 'null';
const data = JSON.parse(rawData);

const csrftoken = document.getElementById('root')?.getAttribute('csrftoken') || 'null';

let pageId = document.getElementById('root')?.getAttribute('page-id');
let PageComponent: React.FC<any>;

if (window.location.search) {
  const searchParams = new URLSearchParams(window.location.search);
  if (searchParams?.has('previewPageId')) {
    pageId = searchParams.get('previewPageId');
  }
}

switch (pageId) {
  case PageId.OnboardingWelcome:
    PageComponent = OnboardingWelcomePage;
    break;
  case PageId.OnboardingVideoTutorial:
    PageComponent = OnboardingVideoTutorialPage;
    break;
  case PageId.OnboardingRecommendations:
    PageComponent = OnboardingRecommendationsPage;
    break;
  case PageId.OnboardingEmergencyContact:
    PageComponent = OnboardingEmergencyContactPage;
    break;
  case PageId.PlacementTestIndex:
    PageComponent = PlacementTestPage;
    break;
  case PageId.PlacementTestIntro:
    PageComponent = PlacementTestIntroductionPage;
    break;
  case PageId.PlacementTestPlayer:
    PageComponent = PlacementTestPlayerPage;
    break;
  case PageId.PlacementTestResult:
    PageComponent = PlacementTestResultPage;
    break;
  case PageId.Help:
    PageComponent = HelpPage;
    break;
  case PageId.Login:
    PageComponent = LoginPage;
    break;
  case PageId.ForgotPassword:
    PageComponent = ForgotPasswordPage;
    break;
  case PageId.ForgotPasswordSuccess:
    PageComponent = ForgotPasswordSuccessPage;
    break;
  case PageId.ForgotPasswordSubmission:
    PageComponent = ForgotPasswordSubmissionPage;
    break;
  case PageId.ForgotPasswordSubmissionSuccess:
    PageComponent = ForgotPasswordSubmissionSuccessPage;
    break;
  case PageId.ForgotUsername:
    PageComponent = ForgotUsernamePage;
    break;
  case PageId.ForgotUsernameSuccess:
    PageComponent = ForgotUsernameSuccessPage;
    break;
  case PageId.Level:
    PageComponent = LevelPage;
    break;
  case PageId.Levels:
    PageComponent = LevelsPage;
    break;
  case PageId.Unit:
    PageComponent = UnitPage;
    break;
  case PageId.Lesson:
    PageComponent = LessonPage;
    break;
  case PageId.PlatformTourVideo:
    PageComponent = PlatformTourVideoPage;
    break;
  case PageId.Play2learnOnboarding:
    PageComponent = Play2LearnOnboardingPage;
    break;
  case PageId.Profile:
    PageComponent = ProfilePage;
    break;
  case PageId.LCHistory:
    PageComponent = LiveClassHistoryPage;
    break;
  case PageId.LessonIndex:
    PageComponent = LessonIndexPage;
    break;
  case PageId.EndOfLevelTest:
    PageComponent = EndOfLevelTestPage;
    break;
  case PageId.Share:
    PageComponent = SharePage;
    break;
  case PageId.Forbidden:
    PageComponent = ForbiddenPage;
    break;
  case PageId.Suspended:
    PageComponent = SuspendedPage;
    break;
  case PageId.NotFound:
    PageComponent = NotFoundPage;
    break;
  case PageId.ZoomInstructions:
    PageComponent = ZoomInstructionsPage;
    break;
  case PageId.Error:
    PageComponent = ErrorPage;
    break;
  case PageId.PreClassActivity:
    PageComponent = PreClassActivityPage;
    break;
  case PageId.Play2learnZoom:
    PageComponent = Play2LearnZoomPage;
    break;
  case PageId.Root:
  default:
    PageComponent = HomePage;
    break;
}

const REACT_QUERY_RETRIES = 3;

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: REACT_QUERY_RETRIES,
      refetchOnWindowFocus: false,
      refetchOnReconnect: true,
    },
  },
});

const App = () => {
  const { environment } = useEnvironment();

  useEffect(() => {
    const measurementId = GOOGLE_ANALYTICS_CONFIG[environment];

    if (!measurementId) {
      return;
    }

    ReactGA.initialize(measurementId);
    ReactGA.send({ hitType: 'pageview', page: pageId });
  }, [environment]);

  return (
    <React.StrictMode>
      <QueryClientProvider client={queryClient}>
        <AppProviders>
          <PageComponent data={data} csrftoken={csrftoken} />
        </AppProviders>
      </QueryClientProvider>
    </React.StrictMode>
  );
};

ReactDOM.render(
  <Suspense fallback={<div />}>
    <App />
  </Suspense>,
  document.getElementById('root'),
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
